import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import { PaginationControls } from 'Angular/features/components/pagination/pagination.constants';
import { PaginationInfo } from '../interfaces/pagination-info.interface';

const defaultPaginationInfo: PaginationInfo = {
    current_page_num: 1,
    num_pages: 1,
    num_items: 1,
    start_index: 1,
    end_index: 1,
};

/**
 * Simple Pagination Controls Component
 *
 * This component provides generic functionality for pagination controls/buttons
 * that are commonly used in components that display large lists of items.
 * It contains Next/Previous buttons and does not include First/Last
 *
 * Input Properties
 *  paginationInfo: PaginationInfo object used for computing the new page when a button is clicked
 *
 * Output Properties
 *  pageChangeHandler: EventEmitter that emits the new page number after the page has been changed
 *
 */
@Component({
    standalone: false,
    selector: 'ycn-simple-pagination-controls',
    templateUrl: './simple-pagination-controls.template.html',
})
export class SimplePaginationControlsComponent implements OnInit, OnChanges {
    @Input() paginationInfo!: PaginationInfo;

    @Output() pageChangeHandler = new EventEmitter<number>();

    PAGE_CONTROLS = PaginationControls;

    pageNum: number = 1;

    _paginationInfo: PaginationInfo = { ...defaultPaginationInfo };

    ngOnInit(): void {
        if (this.paginationInfo) {
            this.pageNum = this.paginationInfo.current_page_num;
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.paginationInfo) {
            this._paginationInfo = { ...defaultPaginationInfo };
            if (changes.paginationInfo.currentValue !== undefined) {
                this._paginationInfo = { ...changes.paginationInfo.currentValue };
            }
        }
        if (changes.paginationInfo?.currentValue) {
            this.pageNum = changes.paginationInfo.currentValue.current_page_num;
        }
    }

    isFirst(): boolean {
        return this.pageNum === 1;
    }

    isLast(): boolean {
        return this.pageNum >= this._paginationInfo.num_pages;
    }

    getPaginationString(): string {
        return `Page ${this._paginationInfo.current_page_num} of ${this._paginationInfo.num_pages}`;
    }

    getResultsString(): string {
        return `${this._paginationInfo.num_items} result${this._paginationInfo.num_items === 1 ? '' : 's'}`;
    }

    /**
     * Uses the current value of pageNum and the paginationEvent to determine the next page number.
     * @param paginationEvent the pagination button that was clicked
     */
    pageChanged(paginationEvent: PaginationControls): void {
        let newPageNum;
        switch (paginationEvent) {
            case this.PAGE_CONTROLS.PREV:
                newPageNum = Math.max(this.pageNum - 1, 1);
                break;
            case this.PAGE_CONTROLS.NEXT:
                newPageNum = Math.min(this.pageNum + 1, this._paginationInfo.num_pages);
                break;
            default:
                newPageNum = 1;
        }
        this.pageNum = newPageNum;
        this.pageChangeHandler.emit(this.pageNum);
    }
}
